import React from 'react';
import { useJeopardyContext } from './jeopardy-context';
import { JeopardyBoardConfig, JeopardyClue } from './jeopardy-types';
import { useQuizContext } from 'contexts';
import { getCssStylesFromQuizSettings } from 'utils/style-utils';

interface Props {
    boardConfig: JeopardyBoardConfig;
    isQuizMaster?: boolean;
    overviewUrl?: string;
    shareUrl?: string;
}

function findClueById(board: JeopardyBoardConfig, id: string, type: 'single' | 'double' | null): JeopardyClue | null {
    const cats = type === 'double' ? board.doubleJeopardyCategories : board.jeopardyCategories;
    for (const cat of cats) {
        const found = cat.clues.find(c => c.id === id);
        if (found) return found;
    }
    return null;
}

function getCategoryForClue(board: JeopardyBoardConfig, clueId: string, type: 'single' | 'double' | null): string {
    const cats = type === 'double' ? board.doubleJeopardyCategories : board.jeopardyCategories;
    for (const cat of cats) {
        if (cat.clues.some(c => c.id === clueId)) return cat.name;
    }
    return '';
}

function getTimerClass(seconds: number, warningThreshold: number): string {
    if (seconds <= 5) return 'jeopardy-player__timer --low';
    if (seconds <= warningThreshold) return 'jeopardy-player__timer --warn';
    return 'jeopardy-player__timer';
}

export const JeopardyPlayerView = ({ boardConfig, isQuizMaster, overviewUrl, shareUrl }: Props) => {
    const { gameState, loading } = useJeopardyContext();
    const { quiz } = useQuizContext();
    const { customTitlePageStyles, customTitleStyles, slideCustomFontStyles, slideCustomBackgroundStyles } = getCssStylesFromQuizSettings(quiz.styles);

    const { phase, teams, playedClues, activeClueId, activeClueType, clueRevealed, remainingSeconds, wagers } = gameState;
    const [displaySeconds, setDisplaySeconds] = React.useState(remainingSeconds);
    const [copyLinkLabel, setCopyLinkLabel] = React.useState('Copy Share Link');

    const activeClue: JeopardyClue | null = activeClueId
        ? findClueById(boardConfig, activeClueId, activeClueType)
        : null;

    const currentCats = phase === 'double_jeopardy' ? boardConfig.doubleJeopardyCategories : boardConfig.jeopardyCategories;
    const sortedTeams = [...teams].sort((a, b) => b.score - a.score);
    const isCountdownActive =
        ((phase === 'single_jeopardy' || phase === 'double_jeopardy') && !!activeClue && !clueRevealed) ||
        phase === 'final_jeopardy_question';

    React.useEffect(() => {
        setDisplaySeconds(remainingSeconds);
    }, [remainingSeconds, phase, activeClueId, clueRevealed]);

    React.useEffect(() => {
        if (!isCountdownActive || displaySeconds <= 0) {
            return;
        }

        const timerId = window.setTimeout(() => {
            setDisplaySeconds(prev => Math.max(prev - 1, 0));
        }, 1000);

        return () => window.clearTimeout(timerId);
    }, [displaySeconds, isCountdownActive]);

    if (loading) {
        return (
            <div className="jeopardy-player jeopardy-player--loading">
                <div className="jeopardy-player__waiting">
                    <h1>🏆 Jeopardy</h1>
                    <p>Connecting to game…</p>
                </div>
            </div>
        );
    }

    const onCopyShareLink = () => {
        if (!shareUrl) return;
        navigator.clipboard.writeText(shareUrl).then(() => {
            setCopyLinkLabel('Copied!');
            setTimeout(() => setCopyLinkLabel('Copy Share Link'), 2000);
        });
    };

    // Lobby / waiting state
    if (phase === 'lobby') {
        return (
            <div className="jeopardy-player jeopardy-player--lobby" style={customTitlePageStyles}>
                <div className="jeopardy-player__waiting">
                    <h1 style={customTitleStyles}>{quiz.name}</h1>
                    {quiz.description && (
                        <p style={slideCustomFontStyles} className="jeopardy-player__lobby-description">
                            {quiz.description}
                        </p>
                    )}
                    {isQuizMaster && (
                        <div className="jeopardy-player__master-lobby-actions">
                            {overviewUrl && (
                                <a
                                    href={overviewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="jeopardy-player__master-action-btn"
                                >
                                    Open Overview
                                </a>
                            )}
                            {shareUrl && (
                                <button
                                    type="button"
                                    className="jeopardy-player__master-action-btn"
                                    onClick={onCopyShareLink}
                                >
                                    {copyLinkLabel}
                                </button>
                            )}
                        </div>
                    )}
                    {teams.length > 0 && (
                        <div className="jeopardy-player__team-list">
                            {teams.map(team => (
                                <div
                                    key={team.id}
                                    className="jeopardy-player__team-badge"
                                    style={{ borderColor: team.color }}
                                >
                                    <span>{team.avatar}</span>
                                    <span>{team.name}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <p className="jeopardy-player__lobby-footnote">Waiting for the Quiz Master to start the game…</p>
                </div>
            </div>
        );
    }

    // Game Over
    if (phase === 'game_over') {
        return (
            <div className="jeopardy-player jeopardy-player--gameover">
                <div className="jeopardy-player__waiting">
                    <h1 style={{ color: 'var(--j-gold)' }}>🏆 Final Results!</h1>
                    <div className="jeopardy-player__podium">
                        {sortedTeams.slice(0, Math.min(3, sortedTeams.length)).map((team, idx) => (
                            <div
                                key={team.id}
                                className={`jeopardy-player__podium-place jeopardy-player__podium-place--${idx + 1}`}
                                style={{ borderColor: team.color }}
                            >
                                <span>{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</span>
                                <span style={{ fontSize: '1.8rem' }}>{team.avatar}</span>
                                <span style={{ fontWeight: 700 }}>{team.name}</span>
                                <span className="jeopardy-player__final-score">${team.score.toLocaleString()}</span>
                            </div>
                        ))}
                    </div>
                    {sortedTeams.length > 3 && sortedTeams.slice(3).map((team, idx) => (
                        <div key={team.id} style={{ color: 'var(--j-muted)', fontSize: '0.9rem', marginTop: 4 }}>
                            {idx + 4}. {team.avatar} {team.name} — ${team.score.toLocaleString()}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Tie-breaker
    if (phase === 'tie_breaker') {
        return (
            <div className="jeopardy-player" style={{ alignItems: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column', gap: 20 }}>
                <h1 style={{ color: 'var(--j-gold)', fontSize: '2.5rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 4 }}>
                    ⚡ Sudden Death!
                </h1>
                <p style={{ color: 'var(--j-muted)', fontSize: '1rem' }}>First correct answer wins the tie!</p>
                <div style={{ display: 'flex', gap: 16 }}>
                    {teams.map(team => (
                        <div key={team.id} style={{ textAlign: 'center', background: 'var(--j-card)', border: `2px solid ${team.color}`, borderRadius: 10, padding: '16px 24px' }}>
                            <div style={{ fontSize: '2rem' }}>{team.avatar}</div>
                            <div style={{ fontWeight: 700 }}>{team.name}</div>
                            <div style={{ color: 'var(--j-gold)', fontWeight: 800 }}>${team.score.toLocaleString()}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="jeopardy-player">
            {/* Header */}
            <div className="jeopardy-player__header">
                <span className="jeopardy-player__title">
                    {phase === 'double_jeopardy' ? 'Double Jeopardy!' : phase.startsWith('final') ? 'Final Jeopardy!' : 'Jeopardy!'}
                </span>
            </div>

            {/* Scoreboard strip */}
            <div className="jeopardy-player__scoreboard">
                {teams.map(team => (
                    <div
                        key={team.id}
                        className="jeopardy-player__score-card"
                        style={{ borderBottomColor: team.color }}
                    >
                        <span className="jeopardy-player__team-avatar">{team.avatar}</span>
                        <span className="jeopardy-player__team-name">{team.name}</span>
                        <span className={`jeopardy-player__team-score${team.score < 0 ? ' --negative' : ''}`}>
                            ${team.score.toLocaleString()}
                        </span>
                    </div>
                ))}
            </div>

            {/* Board grid — always rendered, clue overlays on top */}
            {(phase === 'single_jeopardy' || phase === 'double_jeopardy') && (
                <div className="jeopardy-player__board">
                    <div
                        className="jeopardy-player__board-grid"
                        style={{ gridTemplateColumns: `repeat(${currentCats.length}, 1fr)` }}
                    >
                        {currentCats.map(cat => (
                            <div key={cat.id} className="jeopardy-player__board-category">
                                {cat.name}
                            </div>
                        ))}
                        {Array.from({ length: boardConfig.rowsCount }, (_, rowIdx) =>
                            currentCats.map(cat => {
                                const clue = cat.clues[rowIdx];
                                if (!clue) return null;
                                const played = !!playedClues[clue.id];
                                const isActive = activeClueId === clue.id;
                                return (
                                    <div
                                        key={clue.id}
                                        className={[
                                            'jeopardy-player__board-cell',
                                            played ? '--played' : '',
                                            isActive ? '--active' : '',
                                        ].filter(Boolean).join(' ')}
                                    >
                                        {played ? '' : `$${clue.points}`}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}

            {/* Daily Double waiting state */}
            {gameState.pendingDailyDoubleClueId && !activeClueId && (phase === 'single_jeopardy' || phase === 'double_jeopardy') && (
                <div className="jeopardy-player__clue-overlay">
                    <div className="jeopardy-player__clue-category">Daily Double!</div>
                    <div className="jeopardy-player__clue-points" style={{ color: 'var(--j-green)' }}>⭐ ⭐ ⭐</div>
                    <div style={{ fontSize: '1.1rem', color: 'var(--j-muted)' }}>Wager is being set…</div>
                </div>
            )}

            {/* Active clue overlay — sits on top of the board */}
            {activeClue && (phase === 'single_jeopardy' || phase === 'double_jeopardy') && (
                <div className="jeopardy-player__clue-overlay" style={{ ...slideCustomBackgroundStyles, ...slideCustomFontStyles }}>
                    <div className="jeopardy-player__clue-category">
                        {getCategoryForClue(boardConfig, activeClue.id, activeClueType)}
                    </div>
                    <div className="jeopardy-player__clue-points">
                        {gameState.pendingDailyDoubleWager
                            ? `⭐ Daily Double · $${gameState.pendingDailyDoubleWager}`
                            : `$${activeClue.points}`}
                    </div>
                    <div
                        className="jeopardy-player__clue-question"
                        style={{
                            ...(slideCustomFontStyles.fontSize ? { fontSize: slideCustomFontStyles.fontSize } : {}),
                            ...(slideCustomFontStyles.fontWeight ? { fontWeight: slideCustomFontStyles.fontWeight } : {}),
                            ...(slideCustomFontStyles.fontFamily ? { fontFamily: slideCustomFontStyles.fontFamily } : {}),
                            ...(slideCustomFontStyles.fontStyle ? { fontStyle: slideCustomFontStyles.fontStyle } : {}),
                            ...(slideCustomFontStyles.lineHeight ? { lineHeight: slideCustomFontStyles.lineHeight } : {}),
                        }}
                    >
                        {activeClue.question}
                    </div>
                    {clueRevealed && (
                        <div className="jeopardy-player__clue-answer">{activeClue.answer}</div>
                    )}
                    {!clueRevealed && (
                        <div className={getTimerClass(displaySeconds, 10)}>
                            {displaySeconds}
                        </div>
                    )}
                </div>
            )}

            {/* Final Jeopardy - Wager phase */}
            {phase === 'final_jeopardy_wager' && (
                <div className="jeopardy-player__final">
                    <h2>Final Jeopardy</h2>
                    <p className="jeopardy-player__final-category">
                        Category: <strong>{boardConfig.finalJeopardy.category}</strong>
                    </p>
                    <p className="jeopardy-player__final-instructions">
                        Teams are submitting their wagers…
                    </p>
                    <div className="jeopardy-player__wager-status">
                        {teams.map(team => (
                            <div
                                key={team.id}
                                className="jeopardy-player__wager-team"
                                style={{ borderLeftColor: team.color }}
                            >
                                <span>{team.avatar} {team.name}</span>
                                <span style={{ color: wagers[team.id] !== undefined ? 'var(--j-green)' : 'var(--j-muted)' }}>
                                    {wagers[team.id] !== undefined ? '✓ Wager placed' : 'Pending…'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Final Jeopardy - Question & Evaluation phase */}
            {(phase === 'final_jeopardy_question' || phase === 'final_jeopardy_evaluation') && (
                <div className="jeopardy-player__final">
                    <h2>Final Jeopardy</h2>
                    <p className="jeopardy-player__final-category">
                        Category: <strong>{boardConfig.finalJeopardy.category}</strong>
                    </p>
                    <div className="jeopardy-player__final-question">
                        {boardConfig.finalJeopardy.question}
                    </div>
                    {phase === 'final_jeopardy_question' && (
                        <div className={getTimerClass(displaySeconds, 15)}>
                            {displaySeconds}
                        </div>
                    )}
                    {phase === 'final_jeopardy_evaluation' && clueRevealed && (
                        <div className="jeopardy-player__final-answer">
                            <strong>Answer:</strong> {boardConfig.finalJeopardy.answer}
                        </div>
                    )}
                    {phase === 'final_jeopardy_evaluation' && (
                        <div className="jeopardy-player__eval-status">
                            {teams.map(team => {
                                const eval_ = gameState.finalEvaluations[team.id];
                                return (
                                    <div
                                        key={team.id}
                                        className="jeopardy-player__eval-team"
                                        style={{ borderLeftColor: team.color }}
                                    >
                                        <span>{team.avatar} {team.name}</span>
                                        {eval_ !== undefined && eval_ !== null ? (
                                            <span className={eval_ ? 'jeopardy-player__correct' : 'jeopardy-player__wrong'}>
                                                {eval_ ? `✓ +$${wagers[team.id] ?? 0}` : `✗ −$${wagers[team.id] ?? 0}`}
                                            </span>
                                        ) : (
                                            <span style={{ color: 'var(--j-muted)' }}>…</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
